import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { stripApiSuffix } from '../../utils/apiBaseUrl';

// Backend API Configuration
const BACKEND_API_URL = stripApiSuffix(process.env.REACT_APP_API_URL) || 'http://localhost:3050';

/**
 * Step 2: Upload your claims data
 * Streamlined functionality: file preparation, upload, and automated processing
 */
const Step2Page = () => {
  const [currentSubStep, setCurrentSubStep] = useState(1);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [uploadDetails, setUploadDetails] = useState(null);

  /**
   * Handle file upload to Azure Blob Storage via backend API
   */
  const handleFileUpload = async () => {
    if (!uploadedFile) {
      setUploadError('No file selected');
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      // Step 1: Request SAS URL from backend
      console.log('Requesting SAS URL from backend...');
      const requestPayload = {
        originalFilename: uploadedFile.name,
        contentType: uploadedFile.type || 'application/zip',
        contentLength: uploadedFile.size
      };

      const requestResponse = await fetch(`${BACKEND_API_URL}/api/uploads/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload)
      });

      if (!requestResponse.ok) {
        const errorData = await requestResponse.json();
        throw new Error(errorData.detail?.error_description || 'Failed to get upload URL');
      }

      const uploadData = await requestResponse.json();
      console.log('SAS URL received:', uploadData.blobName);
      setUploadProgress(20);

      // Step 2: Upload file directly to Azure Blob Storage
      console.log('Uploading file to Azure Blob Storage...');
      const uploadStartTime = Date.now();

      const blobUploadResponse = await fetch(uploadData.sasUrl, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob'
          // Note: Content-Type is NOT set because the SAS token signature doesn't include it
        },
        body: uploadedFile
      });

      if (!blobUploadResponse.ok) {
        throw new Error(`Azure upload failed: ${blobUploadResponse.statusText}`);
      }

      const uploadDurationMs = Date.now() - uploadStartTime;
      const eTag = blobUploadResponse.headers.get('ETag') || '';
      console.log('File uploaded successfully to Azure');
      setUploadProgress(80);

      // Step 3: Notify backend of upload completion
      console.log('Notifying backend of upload completion...');
      const completePayload = {
        blobName: uploadData.blobName,
        eTag: eTag,
        contentLength: uploadedFile.size,
        uploadDurationMs: uploadDurationMs,
        clientTraceId: uploadData.requestId
      };

      const completeResponse = await fetch(`${BACKEND_API_URL}/api/uploads/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(completePayload)
      });

      if (!completeResponse.ok) {
        const errorData = await completeResponse.json();
        throw new Error(errorData.detail?.error_description || 'Failed to complete upload');
      }

      const completeData = await completeResponse.json();
      console.log('Upload completed successfully:', completeData);
      setUploadProgress(100);
      setUploadDetails({
        blobName: uploadData.blobName,
        requestId: completeData.requestId,
        auditRecordId: completeData.auditRecordId,
        duration: uploadDurationMs
      });

      // Move to processing step after successful upload
      setTimeout(() => {
        setIsUploading(false);
        setCurrentSubStep(3);
      }, 500);

    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="step-page min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header with back navigation */}
      <div className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link 
                to="/cdi-trial" 
                className="text-primary-600 hover:text-primary-700 flex items-center text-sm font-medium"
              >
                ← Back to CDI Trial
              </Link>
              <div className="h-4 w-px bg-gray-300 dark:bg-gray-600"></div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                Step 2: Upload your claims data
              </h1>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              10 minutes
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          
          {/* Progress indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span>Step 2 of 3</span>
              <span>67% Complete</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div className="bg-primary-600 h-2 rounded-full" style={{ width: '67%' }}></div>
            </div>
          </div>

          {/* Sub-step progress - shows progress within Step 2 */}
          <div className="mb-8">
            <div className="flex items-center justify-center space-x-4">
              {[
                { id: 'prepare', title: "Prepare", completed: currentSubStep > 1 },
                { id: 'upload', title: "Upload", completed: currentSubStep > 2 },
                { id: 'process', title: "Process", completed: currentSubStep > 3 },
                { id: 'preview', title: "Preview", completed: currentSubStep > 4 }
              ].map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className={`w-3 h-3 rounded-full ${
                    currentSubStep === index + 1
                      ? 'bg-primary-600 ring-4 ring-primary-200 dark:ring-primary-800'
                      : step.completed
                        ? 'bg-green-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                  }`}></div>
                  <span className={`ml-2 text-sm ${
                    currentSubStep === index + 1
                      ? 'text-primary-600 font-medium'
                      : step.completed
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {step.title}
                  </span>
                  {index < 3 && (
                    <div className={`w-8 h-0.5 mx-3 ${
                      step.completed ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`}></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            
            {/* Left side - Main workflow */}
            <div className="space-y-6">
              
              {/* Step 1: Prepare File */}
              {currentSubStep === 1 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                  <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                    Prepare Your Claims File
                  </h2>
                  
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 mb-6">
                    <div className="text-blue-600 text-4xl mb-4">📋</div>
                    <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
                      File Preparation Requirements
                    </h3>
                    <p className="text-blue-800 dark:text-blue-200 mb-4">
                      Prepare your claims data as a ZIP file containing CSV or Parquet format files.
                    </p>
                    <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                      <li>• ZIP file containing CSV or Parquet format</li>
                      <li>• Maximum 10,000 claims for trial</li>
                      <li>• Data can be raw (will be anonymized by Videxa) or pre-anonymized</li>
                      <li>• Standard healthcare claims schema recommended</li>
                    </ul>
                  </div>

                  <div className="mt-6">
                    <button 
                      onClick={() => setCurrentSubStep(2)}
                      className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 px-4 rounded-lg transition-colors"
                    >
                      Continue to Upload
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Upload */}
              {currentSubStep === 2 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                  <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                    Upload Your Claims File
                  </h2>
                  
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
                    <div className="text-4xl mb-4">📁</div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Drag and drop your .zip file here
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      Or click to browse and select your file
                    </p>
                    <input 
                      type="file" 
                      accept=".zip"
                      className="hidden"
                      id="file-upload"
                      onChange={(e) => setUploadedFile(e.target.files[0])}
                    />
                    <label 
                      htmlFor="file-upload"
                      className="bg-primary-600 hover:bg-primary-700 text-white py-2 px-4 rounded-lg transition-colors cursor-pointer inline-block"
                    >
                      Select File
                    </label>
                  </div>

                  {uploadedFile && (
                    <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <div className="text-green-600">✓</div>
                        <div>
                          <div className="font-medium text-green-900 dark:text-green-100">
                            File selected: {uploadedFile.name}
                          </div>
                          <div className="text-sm text-green-700 dark:text-green-300">
                            Size: {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {uploadError && (
                    <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <div className="text-red-600">✕</div>
                        <div>
                          <div className="font-medium text-red-900 dark:text-red-100">
                            Upload Failed
                          </div>
                          <div className="text-sm text-red-700 dark:text-red-300">
                            {uploadError}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {isUploading && uploadProgress > 0 && (
                    <div className="mt-6">
                      <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                        <span>Uploading to Azure Storage...</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  <div className="mt-6 flex space-x-4">
                    <button
                      onClick={() => setCurrentSubStep(1)}
                      disabled={isUploading}
                      className="flex-1 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 py-3 px-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleFileUpload}
                      disabled={!uploadedFile || isUploading}
                      className="flex-1 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white py-3 px-4 rounded-lg transition-colors disabled:cursor-not-allowed"
                    >
                      {isUploading ? `Uploading... ${uploadProgress}%` : 'Upload File'}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Processing */}
              {currentSubStep === 3 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                  <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                    Videxa Processing
                  </h2>
                  
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-6 mb-6">
                    <div className="text-purple-600 text-4xl mb-4">🔄</div>
                    <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-2">
                      Automated Processing in Progress
                    </h3>
                    <p className="text-purple-800 dark:text-purple-200 mb-4">
                      Videxa is running virus scan and performing anonymization if not supplied in that manner.
                    </p>
                    
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <div className="text-green-600">✓</div>
                        <span className="text-sm text-purple-800 dark:text-purple-200">Virus scan complete</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="text-green-600">✓</div>
                        <span className="text-sm text-purple-800 dark:text-purple-200">Schema validation passed</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {isProcessing ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                        ) : (
                          <div className="text-green-600">✓</div>
                        )}
                        <span className="text-sm text-purple-800 dark:text-purple-200">
                          {isProcessing ? 'Applying anonymization...' : 'Anonymization complete'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex space-x-4">
                    <button 
                      onClick={() => setCurrentSubStep(2)}
                      className="flex-1 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 py-3 px-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Back
                    </button>
                    <button 
                      onClick={() => {
                        setIsProcessing(true);
                        setTimeout(() => {
                          setIsProcessing(false);
                          setShowPreview(true);
                          setCurrentSubStep(4);
                        }, 3000);
                      }}
                      disabled={isProcessing}
                      className="flex-1 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white py-3 px-4 rounded-lg transition-colors"
                    >
                      {isProcessing ? 'Processing...' : 'Continue to Preview'}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 4: Preview */}
              {currentSubStep === 4 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                  <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                    Preview Anonymized Results
                  </h2>
                  
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6 mb-6">
                    <div className="text-green-600 text-4xl mb-4">👁️</div>
                    <h3 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-2">
                      Anonymization Complete
                    </h3>
                    <p className="text-green-800 dark:text-green-200 mb-4">
                      Your data has been successfully processed and anonymized. Review the results below.
                    </p>
                  </div>

                  {/* Sample preview table */}
                  <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden mb-6">
                    <div className="bg-gray-50 dark:bg-gray-700 px-4 py-2 border-b border-gray-200 dark:border-gray-600">
                      <h4 className="font-medium text-gray-900 dark:text-white">Sample Anonymized Data (first 3 rows)</h4>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead className="bg-gray-100 dark:bg-gray-600">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300">Member ID</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300">Claim ID</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300">Service Date</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-gray-200 dark:border-gray-600">
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">TKN_001234</td>
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">CLM_789456</td>
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">2024-02-15</td>
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">$450.00</td>
                          </tr>
                          <tr className="border-b border-gray-200 dark:border-gray-600">
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">TKN_005678</td>
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">CLM_123789</td>
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">2024-02-20</td>
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">$125.50</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">TKN_009012</td>
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">CLM_456123</td>
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">2024-02-25</td>
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">$789.25</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6 border-l-4 border-blue-500">
                    <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                      📌 Please note:
                    </h4>
                    <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
                      Please review any flagged issues below:
                    </p>
                    <div className="text-xs text-blue-700 dark:text-blue-300">
                      <p>• No critical issues found</p>
                      <p>• 2 minor formatting warnings (automatically corrected)</p>
                    </div>
                  </div>

                  <div className="flex space-x-4">
                    <button 
                      onClick={() => setCurrentSubStep(2)}
                      className="border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 py-2 px-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Re-upload File
                    </button>
                    <div className="flex-1"></div>
                    <button className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg transition-colors">
                      Accept and Continue
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Right side - Information */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#1F2937' }}>
                  📋 What We'll Cover in This Step
                </h3>
                <ul className="space-y-2 text-sm" style={{ color: '#1F2937' }}>
                  <li>• Prepare claims zip with CSV or Parquet format</li>
                  <li>• Drag-and-drop the .zip file and start upload</li>
                  <li>• Videxa runs virus scan and performs anonymization if not supplied in that manner</li>
                  <li>• Preview anonymized results</li>
                  <li>• Fix any flagged issues and re-upload if needed</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#1F2937' }}>
                  ⚠️ File Requirements
                </h3>
                <div className="space-y-2 text-sm" style={{ color: '#1F2937' }}>
                  <p className="font-medium">File format:</p>
                  <ul className="space-y-1 ml-4 text-xs mb-3">
                    <li>• ZIP file containing CSV or Parquet</li>
                    <li>• Maximum 10,000 claims for trial</li>
                    <li>• Data can be raw or pre-anonymized</li>
                  </ul>
                  <p className="font-medium">Recommended fields:</p>
                  <ul className="space-y-1 ml-4 text-xs">
                    <li>• Member ID</li>
                    <li>• Claim ID</li>
                    <li>• Provider NPI</li>
                    <li>• Service dates</li>
                    <li>• Diagnosis codes (ICD-10)</li>
                    <li>• Procedure codes (CPT/HCPCS)</li>
                    <li>• Claim amounts</li>
                    <li>• Current status</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#1F2937' }}>
                  🔒 Videxa Automated Processing
                </h3>
                <div className="space-y-2 text-sm" style={{ color: '#1F2937' }}>
                  <p>Videxa automatically handles:</p>
                  <ul className="space-y-1 ml-4 text-xs">
                    <li>• Virus scanning and security validation</li>
                    <li>• Schema compliance checking</li>
                    <li>• Anonymization (if not already applied)</li>
                    <li>• Data quality assessment</li>
                    <li>• Preview generation for review</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#1F2937' }}>
                  ✅ Streamlined Process
                </h3>
                <div className="space-y-2 text-sm" style={{ color: '#1F2937' }}>
                  <ul className="space-y-1">
                    <li>• No data source selection needed</li>
                    <li>• No manual anonymization configuration</li>
                    <li>• Automated processing and validation</li>
                    <li>• Clear preview before proceeding</li>
                    <li>• Easy re-upload if issues found</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation buttons */}
          <div className="flex justify-between mt-8">
            <Link 
              to="/cdi-trial/step-1" 
              className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              ← Previous Step
            </Link>
            {currentSubStep === 4 && (
              <Link 
                to="/cdi-trial/step-3" 
                className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
              >
                Continue to Step 3 →
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step2Page;

